'use client';

import House from '@mds/icons/House';
import { Box, Button, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { MouseEventHandler, ReactNode } from 'react';
import { SectionDivider } from '../SectionDivider';
import { SectionSubheader } from '../SectionSubheader';
import type { AppSwitcherAccessOptions, AppSwitcherItem } from './AppSwitcher';
import {
  defaultAppSwitcherItems,
  resolveAppSwitcherPanelItems,
  type AppSwitcherPanelResolvedItem,
} from './AppSwitcher.utils';

export interface AppSwitcherPanelProps extends Omit<AppSwitcherAccessOptions, 'auth0' | 'authClient' | 'onAuthError'> {
  /**
   * Section label above the app list.
   * @default 'Neon Apps'
   */
  title?: ReactNode;
  items?: readonly AppSwitcherItem[];
  /**
   * `list` is the standard side-panel pattern (single-column, alphabetical, two-tier).
   * `legacy` is the interim grid fallback for apps that have not adopted the side panel.
   * @default 'list'
   */
  variant?: 'list' | 'legacy';
  /**
   * Grid column count, `legacy` variant only.
   * @default 2
   */
  columns?: 2 | 3;
  /**
   * Defaults to the `href` of the item in `items` whose `id` is `'dashboard'` (that item
   * is filtered out of the app list, since it's represented by this pinned entry
   * instead), falling back to `/dashboard` when neither is available.
   */
  dashboardHref?: string;
  dashboardLabel?: ReactNode;
  dashboardDescription?: ReactNode;
  onDashboardClick?: MouseEventHandler;
  /**
   * Hides the pinned `Go to Dashboard` entry — use when the host page already provides
   * its own way back to the dashboard.
   * @default false
   */
  hideDashboardLink?: boolean;
  /**
   * Forces the permission-gated resolution path even before `accessToken`, `permissions`,
   * `enabledItemIds`, or `idTokenClaims` are available — for hosts (like the legacy
   * `AppSwitcher`) that know permissions are pending from an auth client.
   */
  hasPermissionContext?: boolean;
}

function sortByLabel(items: readonly AppSwitcherPanelResolvedItem[]) {
  return [...items].sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

function AppListItem({ app }: { app: AppSwitcherPanelResolvedItem }) {
  const interactive = !app.disabled && !app.desktopOnly;

  return (
    <ListItem disablePadding divider={false}>
      <ListItemButton
        component={interactive && app.href ? 'a' : 'div'}
        href={interactive ? app.href : undefined}
        target={interactive ? app.target : undefined}
        rel={interactive ? app.rel : undefined}
        onClick={interactive ? app.onClick : undefined}
        selected={app.selected}
        disabled={!interactive}
        divider={false}
        aria-current={app.selected ? 'page' : undefined}
        sx={{ pl: 2 }}
      >
        {app.icon && (
          <ListItemIcon sx={{ mr: 1.5, '.Mds-svgAsset': { width: 20, height: 20 } }}>{app.icon}</ListItemIcon>
        )}
        <ListItemText primary={app.label} secondary={app.desktopOnly ? 'Desktop only' : undefined} />
      </ListItemButton>
    </ListItem>
  );
}

/**
 * App switcher content matching the Figma "side-panel" and "legacy grid" variants.
 *
 * Renders a pinned `Go to Dashboard` entry, a section label, and a two-tier
 * alphabetical app list — apps the user can access, then apps they cannot, never
 * interleaved. Apps the user can access but that aren't supported on this device
 * (`isMobile` + `mobileSupported: false`) stay in the accessible tier with a
 * "Desktop only" caption instead of moving to the no-access tier.
 *
 * Pass `disableScrollArea` on the wrapping `<AccessoryDrawer>` so this component's own
 * internal scroll region applies to the app list only, keeping `Go to Dashboard` and the
 * section label fixed in place.
 */
export function AppSwitcherPanel({
  title = 'Neon Apps',
  items = defaultAppSwitcherItems,
  variant = 'list',
  columns = 2,
  dashboardHref,
  dashboardLabel = 'Go to Dashboard',
  dashboardDescription = 'Overview across all Marex apps',
  onDashboardClick,
  hideDashboardLink = false,
  accessToken,
  appName,
  authDomain,
  disabledItemIds,
  enabledItemIds,
  hasPermissionContext,
  hiddenItemIds,
  idTokenClaims,
  isMobile,
  permissions,
}: AppSwitcherPanelProps) {
  // The dashboard app is represented by the pinned "Go to Dashboard" entry, not a row in
  // the app list, so filter it out here and default the pinned entry's href to its URL.
  const dashboardItem = items.find((item) => item.id === 'dashboard');
  const resolvedDashboardHref = dashboardHref ?? dashboardItem?.href ?? '/dashboard';
  const listableItems = items.filter((item) => item.id !== 'dashboard');

  const resolvedItems = resolveAppSwitcherPanelItems(listableItems, {
    accessToken,
    appName,
    authDomain,
    disabledItemIds,
    enabledItemIds,
    hasPermissionContext,
    hiddenItemIds,
    idTokenClaims,
    isMobile,
    permissions,
  });
  const accessibleApps = sortByLabel(resolvedItems.filter((item) => !item.disabled));
  const noAccessApps = sortByLabel(resolvedItems.filter((item) => item.disabled));

  if (variant === 'legacy') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <SectionSubheader>{title}</SectionSubheader>
          <List
            disablePadding
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              alignContent: 'start',
              gap: 0.5,
              p: 0.5,
            }}
          >
            {[...accessibleApps, ...noAccessApps].map((app) => (
              <AppListItem key={app.id} app={app} />
            ))}
          </List>
        </Box>
        {!hideDashboardLink && (
          <>
            <SectionDivider />
            <Button
              variant="text"
              color="secondary"
              component={resolvedDashboardHref ? 'a' : 'button'}
              href={resolvedDashboardHref}
              onClick={onDashboardClick}
              sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1.5, mx: 1 }}
            >
              <House fontSize="xl" />
              {dashboardLabel}
            </Button>
          </>
        )}
      </Box>
    );
  }

  const appList = (
    <List disablePadding>
      {accessibleApps.map((app) => (
        <AppListItem key={app.id} app={app} />
      ))}
      {noAccessApps.map((app) => (
        <AppListItem key={app.id} app={app} />
      ))}
    </List>
  );

  return (
    <Box
      sx={
        hideDashboardLink
          ? { display: 'flex', flexDirection: 'column', gap: 1 }
          : { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, pt: 1, gap: 1 }
      }
    >
      {!hideDashboardLink && (
        <>
          <ListItemButton
            component={resolvedDashboardHref ? 'a' : 'div'}
            href={resolvedDashboardHref}
            onClick={onDashboardClick}
            divider={false}
            sx={{ pl: 2 }}
          >
            <ListItemIcon sx={{ mr: 1.5 }}>
              <House fontSize="xl" />
            </ListItemIcon>
            <ListItemText primary={dashboardLabel} secondary={dashboardDescription} />
          </ListItemButton>
          <SectionDivider />
        </>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <SectionSubheader>{title}</SectionSubheader>
        {hideDashboardLink ? appList : <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{appList}</Box>}
      </Box>
    </Box>
  );
}
